import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useBlocker } from "react-router-dom";

import type { StoffIntern } from "@repo/shared";

import { api, errorMessage } from "../../lib/api";
import { abgleichBreite, LIEFERANT_NAME, lieferantVon, type Lieferant } from "../../lib/farbabgleich";
import { Bestaetigen } from "./Bestaetigen";

/** „alle“ = jeden JAB-, Sarlas- und ED-Stoff neu abgleichen; „neu“ = nur Stoffe ohne bisherigen Abgleich. */
type Art = "alle" | "neu";
type Kandidat = { stoff: StoffIntern; lieferant: Lieferant; herstellerSchluessel: string };
type Fehlschlag = { id: string; name: string; lieferant: Lieferant; grund: string };
/** Lieferant, der in diesem Lauf mit 502/503 geantwortet hat: Grund aus der API und Zahl seiner übersprungenen Stoffe. */
type Stoerung = { grund: string; uebersprungen: number };
/** Gestörte Lieferanten des Laufs, in der Reihenfolge, in der die Störung auftrat. */
type Stoerungen = Partial<Record<Lieferant, Stoerung>>;
type Lauf = {
  gesamt: number;
  erledigt: number;
  aktuell: string | null;
  ok: number;
  /** Nicht zuzuordnende und übersprungene Stoffe (diese mit „übersprungen – …“). */
  fehlschlaege: Fehlschlag[];
  stoerungen: Stoerungen;
  /** Grund, wegen dem der ganze Lauf vor dem Ende aufgehört hat (Anmeldung, Verbindung, unerwarteter Status); null = keiner. */
  abbruch: string | null;
  /** Über „Abbrechen“ beendet. */
  abgebrochen: boolean;
  fertig: boolean;
};
type Frage = { art: Art; liste: Kandidat[] };
/** Was ein Fehlschlag für den Lauf heißt – siehe `folgeVonStatus`. */
type Folge = "weiter" | "lieferant_ueberspringen" | "lauf_beenden";
type Ergebnis = { ok: true } | { ok: false; grund: string; folge: Folge };

/** Grobe Dauer je Stoff für die Rückfrage – Sarlas liest mehrere gedrosselte Trefferseiten, JAB und ED wenige Aufrufe. */
const SEKUNDEN_JE_STOFF: Record<Lieferant, number> = { jab: 3, sarlas: 8, englisch: 3 };
const SEITE = 200;

/** Zeitpunkt des letzten Abgleichs beim Lieferanten des Stoffs; null = nie abgeglichen. */
const ABGEGLICHEN_AM: Record<Lieferant, (s: StoffIntern) => string | null> = {
  jab: (s) => s.jabAbgeglichenAm,
  sarlas: (s) => s.sarlasAbgeglichenAm,
  englisch: (s) => s.englischAbgeglichenAm,
};

/** Reihenfolge im Lauf: JAB, dann Sarlas, dann ED – unabhängig von der Herstellerliste, die nach Namen sortiert ist. */
const RANG: Record<Lieferant, number> = { jab: 0, sarlas: 1, englisch: 2 };

/** Kandidaten nach Lieferant geordnet; `sort` ist stabil, innerhalb eines Lieferanten bleibt die Reihenfolge. */
function nachLieferant(liste: readonly Kandidat[]): Kandidat[] {
  return [...liste].sort((a, b) => RANG[a.lieferant] - RANG[b.lieferant]);
}

/**
 * Was ein Fehlerstatus der API für den Lauf heißt:
 * - 400/404: Stoff nicht zuzuordnen – er kommt in die Liste, der Lauf geht weiter.
 * - 502/503: Lieferant gestört oder nicht konfiguriert – seine restlichen Stoffe werden übersprungen,
 *   die anderen Lieferanten laufen weiter.
 * - jeder andere (401 Anmeldung abgelaufen, unerwartete Status): der ganze Lauf endet.
 */
function folgeVonStatus(status: number): Folge {
  if (status === 400 || status === 404) return "weiter";
  if (status === 502 || status === 503) return "lieferant_ueberspringen";
  return "lauf_beenden";
}

/**
 * Vor der Anfrage: Ist der Lieferant in diesem Lauf schon gestört, wird der Stoff ohne Anfrage
 * übersprungen – das Portal wird in diesem Lauf nicht weiter angefragt. null = anfragen.
 */
function ueberspringen(k: Kandidat, s: Stoerungen): Ergebnis | null {
  const st = s[k.lieferant];
  return st ? { ok: false, grund: st.grund, folge: "lieferant_ueberspringen" } : null;
}

/** Zählt einen Stoff des gestörten Lieferanten; der erste Vermerk legt den Grund fest. */
function stoerungVermerken(s: Stoerungen, lieferant: Lieferant, grund: string): Stoerungen {
  const bisher = s[lieferant];
  return { ...s, [lieferant]: { grund: bisher?.grund ?? grund, uebersprungen: (bisher?.uebersprungen ?? 0) + 1 } };
}

/** Text in der Liste der nicht abgeglichenen Stoffe; Stoffe eines gestörten Lieferanten als „übersprungen – <Grund>“. */
function fehlschlagGrund(e: { grund: string; folge: Folge }): string {
  return e.folge === "lieferant_ueberspringen" ? `übersprungen – ${e.grund}` : e.grund;
}

function anzahlUebersprungen(s: Stoerungen): number {
  return Object.values(s).reduce((n, st) => n + (st?.uebersprungen ?? 0), 0);
}

/** Z. B. „ED gestört – 37 ED-Stoffe übersprungen (ED gerade nicht erreichbar)“. */
function stoerungText(lieferant: Lieferant, st: Stoerung): string {
  const name = LIEFERANT_NAME[lieferant];
  return `${name} gestört – ${st.uebersprungen} ${name}-${st.uebersprungen === 1 ? "Stoff" : "Stoffe"} übersprungen (${st.grund})`;
}

/** Je gestörtem Lieferanten ein Hinweis – während des Laufs und in der Zusammenfassung. */
function stoerungsHinweise(s: Stoerungen): ReactNode {
  return (Object.keys(s) as Lieferant[]).map((l) => {
    const st = s[l];
    return (
      st && (
        <p key={l} className="warnung">
          {stoerungText(l, st)}
        </p>
      )
    );
  });
}

/**
 * Aktive Stoffe der Lieferanten mit Farbabgleich (JAB, Sarlas, ED), bei „neu“ nur die noch nie abgeglichenen –
 * nach Lieferant geordnet (siehe `RANG`).
 */
async function kandidatenLaden(art: Art): Promise<Kandidat[]> {
  const res = await api.api.admin.hersteller.$get();
  if (!res.ok) throw new Error(await errorMessage(res, "Hersteller nicht ladbar"));
  const daten = await res.json();
  const hersteller = "hersteller" in daten ? daten.hersteller : [];
  const liste: Kandidat[] = [];
  for (const h of hersteller) {
    const lieferant = lieferantVon(h.schluessel);
    if (!lieferant) continue;
    for (let offset = 0; ; offset += SEITE) {
      const r = await api.api.admin.stoffe.$get({
        query: { hersteller: h.id, limit: String(SEITE), offset: String(offset), sort: "name", richtung: "asc" },
      });
      if (!r.ok) throw new Error(await errorMessage(r, "Stoffe nicht ladbar"));
      const seite = await r.json();
      if (!("stoffe" in seite)) break;
      for (const stoff of seite.stoffe) {
        const abgeglichen = ABGEGLICHEN_AM[lieferant](stoff);
        if (art === "alle" || !abgeglichen) liste.push({ stoff, lieferant, herstellerSchluessel: h.schluessel });
      }
      if (seite.stoffe.length < SEITE || offset + SEITE >= seite.gesamt) break;
    }
  }
  return nachLieferant(liste);
}

/** Abgleich-Anfrage beim Lieferanten des Stoffs; ED bekommt zusätzlich die Ballenbreite (nur eine positive ganze Zahl). */
function farbenAnfragen(k: Kandidat) {
  const json = { herstellerSchluessel: k.herstellerSchluessel, name: k.stoff.name, nummer: k.stoff.nummer ?? "", stoffId: k.stoff.id };
  switch (k.lieferant) {
    case "jab":
      return api.api.admin.jab.farben.$post({ json });
    case "sarlas":
      return api.api.admin.sarlas.farben.$post({ json });
    case "englisch": {
      const breiteMm = abgleichBreite(k.stoff.ballenbreiteMm);
      return api.api.admin.englisch.farben.$post({ json: { ...json, ...(breiteMm !== undefined ? { breiteMm } : {}) } });
    }
  }
}

/**
 * Ein Stoff – genau wie der Abgleich-Knopf in der Stoffmaske, mit `stoffId`, also sofort gespeichert.
 * Nicht zuzuordnen (400/404) geht weiter. Ist der Lieferant gestört oder nicht konfiguriert (502/503),
 * werden seine restlichen Stoffe übersprungen, damit sein Portal nicht weiter angefragt wird – die anderen
 * Lieferanten laufen weiter. Abgelaufene Anmeldung (401) und jeder andere Status beenden den ganzen Lauf.
 */
async function abgleichen(k: Kandidat): Promise<Ergebnis> {
  const res = await farbenAnfragen(k);
  if (res.ok) {
    const d = await res.json();
    if ("gespeichert" in d && d.gespeichert) return { ok: true };
    return { ok: false, grund: "Farben gefunden, aber nicht gespeichert", folge: "weiter" };
  }
  const grund = await errorMessage(res, "Abgleich fehlgeschlagen");
  return { ok: false, grund, folge: folgeVonStatus(res.status) };
}

function dauerText(liste: Kandidat[]): string {
  const sekunden = liste.reduce((s, k) => s + SEKUNDEN_JE_STOFF[k.lieferant], 0);
  const minuten = Math.max(1, Math.round(sekunden / 60));
  return minuten === 1 ? "etwa eine Minute" : `etwa ${minuten} Minuten`;
}

function frageText(f: Frage): ReactNode {
  const anzahl = (l: Lieferant) => f.liste.filter((k) => k.lieferant === l).length;
  return (
    <>
      {f.liste.length} Stoffe (JAB {anzahl("jab")}, Sarlas {anzahl("sarlas")}, ED {anzahl("englisch")}) werden nacheinander
      abgeglichen – das dauert {dauerText(f.liste)}. Die
      Seite muss dabei geöffnet bleiben.
      {f.art === "alle" && " Bereits gespeicherte Farben werden durch den neuen Abgleich ersetzt."}
    </>
  );
}

/**
 * Farbabgleich für viele Stoffe aus der Stoffliste, im Browser gesteuert und streng nacheinander –
 * die Server-Clients drosseln zusätzlich. Liefert die Knöpfe (für die Kopfzeile) und die
 * Fortschrittsanzeige (unter der Kopfzeile) getrennt.
 */
export function useFarbabgleichLauf(onFertig: () => void): { knoepfe: ReactNode; anzeige: ReactNode } {
  const [vorbereitung, setVorbereitung] = useState<Art | null>(null);
  const [frage, setFrage] = useState<Frage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [lauf, setLauf] = useState<Lauf | null>(null);
  const abbrechen = useRef(false);
  const laeuft = lauf !== null && !lauf.fertig;

  // Verlässt die Seite (Unmount), endet der Lauf nach dem gerade laufenden Stoff.
  useEffect(
    () => () => {
      abbrechen.current = true;
    },
    [],
  );

  // Tab schließen oder neu laden: Browser fragt nach.
  useEffect(() => {
    if (!laeuft) return;
    const warnen = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnen);
    return () => window.removeEventListener("beforeunload", warnen);
  }, [laeuft]);

  // Navigation innerhalb der App: nachfragen, sonst bricht der Lauf ab.
  const blocker = useBlocker(laeuft);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("Der Farbabgleich läuft noch. Seite verlassen und den Abgleich abbrechen?")) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  async function vorbereiten(art: Art) {
    setHinweis(null);
    setLauf(null);
    setVorbereitung(art);
    try {
      const liste = await kandidatenLaden(art);
      if (liste.length === 0) {
        setHinweis(art === "neu" ? "Alle JAB-, Sarlas- und ED-Stoffe sind bereits abgeglichen." : "Keine JAB-, Sarlas- oder ED-Stoffe gefunden.");
      } else {
        setFrage({ art, liste });
      }
    } catch (e) {
      setHinweis(e instanceof Error ? e.message : "Stoffe nicht ladbar");
    } finally {
      setVorbereitung(null);
    }
  }

  async function starten(liste: Kandidat[]) {
    abbrechen.current = false;
    let ok = 0;
    const fehlschlaege: Fehlschlag[] = [];
    let stoerungen: Stoerungen = {};
    let abbruch: string | null = null;
    let abgebrochen = false;
    setLauf({
      gesamt: liste.length,
      erledigt: 0,
      aktuell: null,
      ok: 0,
      fehlschlaege: [],
      stoerungen: {},
      abbruch: null,
      abgebrochen: false,
      fertig: false,
    });
    for (const [i, k] of liste.entries()) {
      if (abbrechen.current) {
        abgebrochen = true;
        break;
      }
      let ergebnis = ueberspringen(k, stoerungen);
      if (ergebnis === null) {
        setLauf((l) => l && { ...l, aktuell: `${k.stoff.name} (${LIEFERANT_NAME[k.lieferant]})` });
        try {
          ergebnis = await abgleichen(k);
        } catch {
          // Unsere API ist nicht erreichbar – das betrifft alle Lieferanten.
          ergebnis = { ok: false, grund: "Keine Verbindung zum Server", folge: "lauf_beenden" };
        }
      }
      if (ergebnis.ok) ok += 1;
      else {
        if (ergebnis.folge === "lieferant_ueberspringen") stoerungen = stoerungVermerken(stoerungen, k.lieferant, ergebnis.grund);
        fehlschlaege.push({ id: k.stoff.id, name: k.stoff.name, lieferant: k.lieferant, grund: fehlschlagGrund(ergebnis) });
      }
      setLauf((l) => l && { ...l, erledigt: i + 1, ok, fehlschlaege: [...fehlschlaege], stoerungen });
      if (!ergebnis.ok && ergebnis.folge === "lauf_beenden") {
        abbruch = ergebnis.grund;
        break;
      }
    }
    setLauf((l) => l && { ...l, aktuell: null, abbruch, abgebrochen, fertig: true });
    onFertig();
  }

  const knoepfe = (
    <>
      <button
        type="button"
        className="stoffe-pill"
        disabled={laeuft || vorbereitung !== null}
        onClick={() => void vorbereiten("alle")}
        title="Alle JAB-, Sarlas- und ED-Stoffe nacheinander neu abgleichen"
      >
        {vorbereitung === "alle" ? "Lade Stoffe …" : "Alle Farben abgleichen"}
      </button>
      <button
        type="button"
        className="stoffe-pill"
        disabled={laeuft || vorbereitung !== null}
        onClick={() => void vorbereiten("neu")}
        title="Nur Stoffe abgleichen, bei denen noch kein Farbabgleich gemacht wurde"
      >
        {vorbereitung === "neu" ? "Lade Stoffe …" : "Aktualisieren"}
      </button>
    </>
  );

  const uebersprungen = lauf ? anzahlUebersprungen(lauf.stoerungen) : 0;
  const anzeige = (
    <>
      <Bestaetigen
        offen={frage !== null}
        titel={frage?.art === "neu" ? "Fehlende Farbabgleiche nachholen?" : "Alle Farben abgleichen?"}
        text={frage ? frageText(frage) : undefined}
        bestaetigen="Starten"
        onBestaetigen={() => {
          const liste = frage?.liste ?? [];
          setFrage(null);
          void starten(liste);
        }}
        onAbbrechen={() => setFrage(null)}
      />
      {hinweis && (
        <p className="farbabgleich-lauf farbabgleich-lauf--hinweis" role="status">
          {hinweis}
          <button type="button" className="stoffe-pill" onClick={() => setHinweis(null)}>
            Schließen
          </button>
        </p>
      )}
      {lauf && (
        <div className="farbabgleich-lauf" role="status" aria-live="polite">
          {!lauf.fertig ? (
            <>
              <div className="farbabgleich-lauf__zeile">
                <span className="lade-kreis" aria-hidden />
                <strong>
                  Farbabgleich läuft – {lauf.erledigt} / {lauf.gesamt}
                </strong>
                {lauf.aktuell && <span className="muted">{lauf.aktuell}</span>}
                <button
                  type="button"
                  className="stoffe-pill farbabgleich-lauf__abbrechen"
                  onClick={() => {
                    abbrechen.current = true;
                  }}
                >
                  Abbrechen
                </button>
              </div>
              <progress max={lauf.gesamt} value={lauf.erledigt} aria-label="Fortschritt des Farbabgleichs" />
              {stoerungsHinweise(lauf.stoerungen)}
            </>
          ) : (
            <>
              <div className="farbabgleich-lauf__zeile">
                <strong>
                  Farbabgleich {lauf.abbruch || lauf.abgebrochen ? "beendet" : "fertig"}: {lauf.ok} abgeglichen ·{" "}
                  {lauf.fehlschlaege.length - uebersprungen} nicht gefunden
                  {uebersprungen > 0 && ` · ${uebersprungen} übersprungen`}
                  {lauf.erledigt < lauf.gesamt && ` · ${lauf.gesamt - lauf.erledigt} nicht bearbeitet`}
                </strong>
                <button
                  type="button"
                  className="stoffe-pill farbabgleich-lauf__abbrechen"
                  onClick={() => setLauf(null)}
                >
                  Schließen
                </button>
              </div>
              {stoerungsHinweise(lauf.stoerungen)}
              {lauf.abbruch && <p className="warnung">{lauf.abbruch}</p>}
              {lauf.abgebrochen && <p className="muted">Abgebrochen.</p>}
              {lauf.fehlschlaege.length > 0 && (
                <details>
                  <summary>Nicht abgeglichene Stoffe ({lauf.fehlschlaege.length})</summary>
                  <ul className="farbabgleich-lauf__liste">
                    {lauf.fehlschlaege.map((f) => (
                      <li key={f.id}>
                        <Link to={`/stoffe/${f.id}`}>{f.name}</Link>{" "}
                        <span className="muted">
                          ({LIEFERANT_NAME[f.lieferant]}) – {f.grund}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </>
  );

  return { knoepfe, anzeige };
}
